import { useRef, useEffect } from 'react';
import { cn } from '@/utils/classNames.ts';
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/24/solid';

type Option = { value: string; label: string };

type MultiSelectInputProps = {
  value: string[];
  onChange: (value: string[]) => void;
  options: Option[];
  placeholder?: string;
  label?: string;
  error?: string;
  wrapperClassName?: string;
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
};

const MultiSelectInput = ({
  value,
  onChange,
  options,
  placeholder = 'Select...',
  label,
  error,
  wrapperClassName,
  isOpen,
  onToggle,
  onClose,
}: MultiSelectInputProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const toggle = (optValue: string) => {
    if (value.includes(optValue)) {
      onChange(value.filter((v) => v !== optValue));
    } else {
      onChange([...value, optValue]);
    }
  };

  const displayText = value
    .map((v) => options.find((o) => o.value === v)?.label)
    .filter(Boolean)
    .join(', ');

  return (
    <div className={cn('form-control max-w-full w-full relative', wrapperClassName)} ref={wrapperRef}>
      {label && (
        <label className="label">
          <span className="label-text">{label}</span>
        </label>
      )}
      <label
        className={cn(
          'input flex items-center justify-between gap-2 cursor-pointer max-w-full input-bordered input-md select-none',
          { 'input-error': !!error }
        )}
        onClick={onToggle}
      >
        <span
          className={cn('text-base grow overflow-hidden text-ellipsis whitespace-nowrap', {
            'opacity-40': !displayText,
          })}
        >
          {displayText || placeholder}
        </span>
        <ChevronDownIcon
          className={cn('w-4 shrink-0 transition-transform duration-150', { 'rotate-180': isOpen })}
        />
      </label>

      {isOpen && (
        <ul className="menu bg-base-100 rounded-box z-20 shadow p-2 w-full absolute top-[calc(100%+4px)] left-0 max-h-[220px] overflow-y-auto flex-nowrap">
          {options.map((opt) => {
            const selected = value.includes(opt.value);
            return (
              <li key={opt.value}>
                <button
                  type="button"
                  className={cn(
                    'flex items-center justify-between px-4 py-2.5 rounded-lg transition-colors',
                    selected
                      ? 'bg-gray-200 text-black font-medium hover:bg-gray-300'
                      : 'hover:bg-gray-100 text-gray-800'
                  )}
                  onClick={() => toggle(opt.value)}
                >
                  <span>{opt.label}</span>
                  {selected && <CheckIcon className="w-4 h-4 shrink-0 text-black" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {error && (
        <label className="label">
          <span className="label-text-alt text-error">{error}</span>
        </label>
      )}
    </div>
  );
};

export default MultiSelectInput;
